
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../base/Button';
import ApiService from '../../services/api2';
import type { User } from '../../services/api2';
import { useLanguageMode, bilingual, LanguageToggle } from '../../i18n/bilingual'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useLanguageMode();

  const navigation = [
    { nameDe: 'Startseite', nameEn: 'Home', href: '/' },
    { nameDe: 'Pläne', nameEn: 'Plans', href: '/plans' },
    { nameDe: 'Dashboard', nameEn: 'Dashboard', href: '/dashboard' },
  ];

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const auth = await ApiService.checkAuth();
        if (!mounted) return;
        setIsAuthenticated(auth.isAuthenticated);
        setUser(auth.user);
      } catch {
        const token = localStorage.getItem('token');
        setIsAuthenticated(!!token);
        const userData = localStorage.getItem('user');
        if (userData) {
          setUser(JSON.parse(userData));
        }
      }
    })();
    const onAuthUpdated = () => {
      const token = localStorage.getItem('token');
      const u = localStorage.getItem('user');
      setIsAuthenticated(!!token);
      setUser(u ? JSON.parse(u) : null);
    };
    window.addEventListener('nutribot:auth-updated', onAuthUpdated as any);
    return () => { mounted = false; window.removeEventListener('nutribot:auth-updated', onAuthUpdated as any); };
  }, []);


  const handleLogout = async () => {
    try {
      await ApiService.logout();
    } catch {}
    setIsAuthenticated(false);
    setUser(null);
    navigate('/signin');
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo-nutribot.svg" alt="NutriBot Logo" className="h-8 w-auto" />
            <span className="ml-2 text-white text-sm sm:text-base font-semibold tracking-wide">NutriBot</span>
          </Link>

          {/* Language Toggle */}
          <div className="hidden md:flex items-center ml-4">
            <LanguageToggle />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium transition-colors duration-200 hover:text-yellow-400 ${
                  isActive(item.href) ? 'text-yellow-400' : 'text-gray-300'
                }`}
              >
                {bilingual(mode, item.nameDe, item.nameEn)}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              (() => {
                const displayName = (user?.name && user.name.trim())
                  || [user && (user as any).firstName, user && (user as any).lastName].filter(Boolean).join(' ').trim()
                  || (user?.email || '').trim()
                  || 'Profil';
                return (
                  <>
                    <Link to="/dashboard" className="text-gray-300 hover:text-yellow-400 text-sm font-medium">
                      {displayName}
                    </Link>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleLogout}
                      className="border border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400 whitespace-nowrap cursor-pointer"
                    >
                      {bilingual(mode, 'Abmelden', 'Log out')}
                    </Button>
                  </>
                );
              })()
            ) : (
              <>
                <Link to="/signin">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="border border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400 whitespace-nowrap cursor-pointer"
                  >
                    {bilingual(mode, 'Anmelden', 'Sign in')}
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button
                    variant="primary"
                    size="sm"
                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 whitespace-nowrap cursor-pointer"
                  >
                    {bilingual(mode, 'Jetzt starten', 'Get started')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Language Toggle (always visible on mobile header) */}
          <div className="md:hidden flex items-center mr-2">
            <LanguageToggle />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-300 hover:text-white transition-colors duration-200 cursor-pointer"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-gray-800 py-4"
          >
            {/* Mobile Language Toggle */}
            <div className="flex justify-start mb-4">
              <LanguageToggle />
            </div>
            <nav className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-yellow-400 ${
                    isActive(item.href) ? 'text-yellow-400' : 'text-gray-300'
                  }`}
                >
                  {bilingual(mode, item.nameDe, item.nameEn)}
                </Link>
              ))}
              <div className="flex flex-col space-y-2 pt-4 border-t border-gray-800">
                {isAuthenticated ? (
                  (() => {
                    const displayName = (user?.name && user.name.trim())
                      || [user && (user as any).firstName, user && (user as any).lastName].filter(Boolean).join(' ').trim()
                      || (user?.email || '').trim()
                      || bilingual(mode, 'Profil', 'Profile');
                    return (
                      <>
                        <Link to="/dashboard" onClick={() => setIsMenuOpen(false)} className="text-gray-300 hover:text-yellow-400 text-sm font-medium">
                          {displayName}
                        </Link>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => { setIsMenuOpen(false); handleLogout(); }}
                          className="border border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400 whitespace-nowrap cursor-pointer w-full"
                        >
                          {bilingual(mode, 'Abmelden', 'Log out')}
                        </Button>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <Link to="/signin" onClick={() => setIsMenuOpen(false)}>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="border border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400 whitespace-nowrap cursor-pointer w-full"
                      >
                        {bilingual(mode, 'Anmelden', 'Sign in')}
                      </Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                      <Button
                        variant="primary"
                        size="sm"
                        className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 whitespace-nowrap cursor-pointer w-full"
                      >
                        {bilingual(mode, 'Jetzt starten', 'Get started')}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
